import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  User, Calendar, DollarSign, BookOpen,
  ChevronRight, ChevronLeft, Check, Search,
  AlertCircle, CheckCircle2, Loader2
} from 'lucide-react';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Navbar';
import api from '../../services/api_client';
import './ContratForm.css';


const STATUTS_CONFIG = {
  BROUILLON:           { code: 'BROUILLON',           label: 'Brouillon' },
  COMMUNIQUE_EN_COURS: { code: 'COMMUNIQUE_EN_COURS', label: 'Communiqué (en cours)' },
  SIGNE:               { code: 'SIGNE',               label: 'Signé' },
  ACTIF:               { code: 'ACTIF',               label: 'Actif' },
  FIN_CDD:             { code: 'FIN_CDD',             label: 'Fin CDD' },
  DEMISSION_CDI:       { code: 'DEMISSION_CDI',       label: 'Démission (CDI)' },
  PAS_DISCUTE:         { code: 'PAS_DISCUTE',         label: 'Pas discuté' },
  INACTIF:             { code: 'INACTIF',             label: 'Inactif (archivé)' },
};

function normaliseStatutCode(s) {
  if (!s) return 'BROUILLON';
  const val = String(s).trim();
  const map = {
    'BROUILLON': 'BROUILLON',
    'Brouillon': 'BROUILLON',
    'COMMUNIQUE_EN_COURS': 'COMMUNIQUE_EN_COURS',
    'Communiqué (en cours)': 'COMMUNIQUE_EN_COURS',
    'SIGNE': 'SIGNE',
    'Signé': 'SIGNE',
    'ACTIF': 'ACTIF',
    'Actif': 'ACTIF',
    'FIN_CDD': 'FIN_CDD',
    'Fin CDD': 'FIN_CDD',
    'DEMISSION_CDI': 'DEMISSION_CDI',
    'Démission (CDI)': 'DEMISSION_CDI',
    'PAS_DISCUTE': 'PAS_DISCUTE',
    'Pas discuté': 'PAS_DISCUTE',
    'INACTIF': 'INACTIF',
    'Inactif (archivé)': 'INACTIF',
    'Inactif': 'INACTIF',
    'Suspendu': 'INACTIF',
    'Terminé': 'INACTIF',
    'Expiré': 'FIN_CDD',
  };
  return map[val] || val;
}

function getAvailableStatuts(statutActuel, typeContrat) {
  const code = normaliseStatutCode(statutActuel);
  const tc = (typeContrat || 'CDI').toUpperCase();

  let nextCodes = [code];

  if (code === 'BROUILLON') {
    nextCodes = ['BROUILLON', 'COMMUNIQUE_EN_COURS', 'PAS_DISCUTE'];
  } else if (code === 'COMMUNIQUE_EN_COURS') {
    nextCodes = ['COMMUNIQUE_EN_COURS', 'SIGNE'];
  } else if (code === 'SIGNE') {
    nextCodes = ['SIGNE', 'ACTIF'];
  } else if (code === 'ACTIF') {
    if (tc === 'CDI') {
      nextCodes = ['ACTIF', 'DEMISSION_CDI'];
    } else {
      nextCodes = ['ACTIF', 'FIN_CDD'];
    }
  } else if (code === 'FIN_CDD') {
    nextCodes = ['FIN_CDD', 'INACTIF'];
  } else if (code === 'DEMISSION_CDI') {
    nextCodes = ['DEMISSION_CDI', 'INACTIF'];
  } else if (code === 'PAS_DISCUTE') {
    nextCodes = ['PAS_DISCUTE', 'COMMUNIQUE_EN_COURS', 'INACTIF'];
  } else if (code === 'INACTIF') {
    nextCodes = ['INACTIF'];
  }

  return nextCodes.map(c => ({
    code: c,
    label: STATUTS_CONFIG[c]?.label || c
  }));
}


const TYPES_CONTRAT = [
  'CDI',
  'CDD',
  'Stage',
  'Alternance',
  'CIVP',
  'Freelance',
  'Indépendant',
];

function isArticleCompatible(article, type) {
  if (!type) return true;
  if (!article.types_contrat || !Array.isArray(article.types_contrat) || article.types_contrat.length === 0) {
    return true; // Compatible avec tous les types (générique)
  }
  return article.types_contrat.map(t => String(t).toUpperCase()).includes(String(type).toUpperCase());
}

const STEPS = [
  { id: 1, label: 'Candidat',     icon: User },
  { id: 2, label: 'Informations', icon: Calendar },
  { id: 3, label: 'Articles',     icon: BookOpen },
];

export default function ContratForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

// Step 1 - Candidat
const [candidats, setCandidats] = useState([]);
const [loadingCandidats, setLoadingCandidats] = useState(false);
const [searchCandidat, setSearchCandidat] = useState('');
const [selectedCandidat, setSelectedCandidat] = useState(null);

  // Step 2 - Informations
  const [typeContrat, setTypeContrat]     = useState('');
  const [dateDebut, setDateDebut]         = useState('');
  const [dateFin, setDateFin]             = useState('');
  const [salaire, setSalaire]             = useState('');
  const [statut, setStatut]               = useState('Brouillon');
  const [initialStatut, setInitialStatut] = useState('Brouillon');

  // Step 3 - Articles
  const [articles, setArticles]           = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);
  const [searchArticle, setSearchArticle] = useState('');

  // Copie de l'état d'origine du contrat pour détecter les modifications
  const [originalContrat, setOriginalContrat] = useState(null);

  // â”€â”€â”€ Chargement initial â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    useEffect(() => {
      loadCandidats();
      loadArticles();

      if (isEdit) {
        loadContrat();
      }
    }, [isEdit, id]);

  const loadCandidats = async () => {
    setLoadingCandidats(true);
    setSubmitError('');

    try {
      const res = await api.get('/recruitment/applications');
      const payload = res?.data;

      console.log('[ContratForm] Payload applications reçu:', payload);

      const applications =
        Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.items)
              ? payload.items
              : [];

      console.log('[ContratForm] Applications reçues:', applications);

      const acceptedApplications = applications.filter((application) => {
        const applicationStatus = String(application?.status || '').trim().toUpperCase();
        const candidateStatus = String(
          application?.candidate?.status ||
          application?.candidat?.status ||
          ''
        ).trim().toUpperCase();

        return applicationStatus === 'ACCEPTED' || candidateStatus === 'ACCEPTED';
      });

      console.log('[ContratForm] Applications ACCEPTED:', acceptedApplications);

      const candidates = acceptedApplications
        .map((application) => {
          if (application?.candidate) return application.candidate;
          if (application?.candidat) return application.candidat;
          if (application?.candidate_id) {
            return {
              id: application.candidate_id,
              first_name: application.first_name || application.prenom || '',
              last_name: application.last_name || application.nom || '',
              email: application.email || '',
              phone: application.phone || application.telephone || '',
              status: 'ACCEPTED',
            };
          }
          return null;
        })
        .filter((candidate) => candidate && candidate.id != null)
        .map((candidate) => ({
          ...candidate,
          status: String(candidate.status || 'ACCEPTED').trim().toUpperCase(),
        }));

      const uniqueCandidates = Array.from(
        new Map(candidates.map((candidate) => [candidate.id, candidate])).values()
      );

      console.log('[ContratForm] Candidats ACCEPTED disponibles:', uniqueCandidates);
      setCandidats(uniqueCandidates);
    } catch (error) {
      console.error('[ContratForm] Erreur chargement candidats:', error?.response?.data || error);
      setCandidats([]);
      setSubmitError(
        error?.response?.data?.detail ||
        'Impossible de récupérer les candidats acceptés.'
      );
    } finally {
      setLoadingCandidats(false);
    }
  };

  const loadArticles = async () => {
  setLoadingArticles(true);

  try {
    const res = await api.get('/articles');

    const data =
      Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.items)
            ? res.data.items
            : [];

    console.log(
      '[ContratForm] Articles reçus:',
      data
    );

    console.table(
      data.map((article) => ({
        id: article?.id,
        code: article?.code,
        titre: article?.titre,
        est_actif: article?.est_actif,
        types_contrat: article?.types_contrat,
      }))
    );

    const activeArticles = data.filter(
      (article) => article?.est_actif === true
    );

    console.log(
      '[ContratForm] Articles actifs:',
      activeArticles
    );

    setArticles(activeArticles);

  } catch (error) {
    console.error(
      '[ContratForm] Erreur chargement articles:',
      error?.response?.data || error
    );

    console.error(
      '[ContratForm] URL:',
      error?.config?.url
    );

    setArticles([]);

    setSubmitError(
      error?.response?.data?.detail ||
      'Impossible de charger les articles.'
    );
  } finally {
    setLoadingArticles(false);
  }
};

  const loadContrat = async () => {
    try {
      const res = await api.get(`/contrats/${id}`);
      const c = res.data;
      setOriginalContrat(c);
      setTypeContrat(c.type_contrat || '');
      setDateDebut(c.date_debut || '');
      setDateFin(c.date_fin || '');
      setSalaire(String(c.salaire_mensuel || ''));
      const sCode = normaliseStatutCode(c.statut);
      setStatut(sCode);
      setInitialStatut(sCode);
      setSelectedArticleIds((c.articles || []).map(a => a.id));
      // Compatibilité avec les réponses actuelles/anciennes
      if (c.candidat) {
        setSelectedCandidat(c.candidat);
      } else if (c.employe) {
        setSelectedCandidat({
          id: c.employe.id,
          first_name: c.employe.first_name || c.employe.prenom || '',
          last_name: c.employe.last_name || c.employe.nom || '',
          email: c.employe.email || '',
          phone: c.employe.phone || c.employe.telephone || '',
          matricule: c.employe.matricule || '',
          status: 'ACCEPTED',
        });
      }
    } catch {
      setSubmitError('Impossible de charger le contrat.');
    }
  };

  // Changer de type de contrat : reinitialiser la date de fin si on passe a CDI
  // et deselectionner automatiquement les articles incompatibles
  const handleTypeContratChange = (value) => {
    setTypeContrat(value);
    if (value === 'CDI') {
      setDateFin('');
    }
    if (value) {
      setSelectedArticleIds(prev =>
        prev.filter(id => {
          const art = articles.find(a => a.id === id);
          return art ? isArticleCompatible(art, value) : true;
        })
      );
    }
  };

  // â”€â”€â”€ Navigation entre étapes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const validateStep = (s) => {
    if (s === 1) return !!selectedCandidat;
    if (s === 2) {
      if (!typeContrat) return false;
      if (dateDebut.trim() === '' || salaire.trim() === '' || Number(salaire) <= 0) return false;
      if (typeContrat !== 'CDI') {
        if (!dateFin) return false;
        if (dateFin <= dateDebut) return false;
      }
      return true;
    }
    return true;
  };

  const goNext = () => {
    if (validateStep(step)) setStep(s => s + 1);
  };

  const goPrev = () => setStep(s => s - 1);

  // â”€â”€â”€ Toggle article â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const toggleArticle = (artId) => {
    setSelectedArticleIds(prev =>
      prev.includes(artId) ? prev.filter(x => x !== artId) : [...prev, artId]
    );
  };

  // â”€â”€â”€ Vérification des modifications â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const checkIfContratModified = () => {
    if (!originalContrat) return false;
    if (typeContrat !== (originalContrat.type_contrat || '')) return true;
    if (dateDebut !== (originalContrat.date_debut || '')) return true;
    const origFin = originalContrat.date_fin || '';
    const currFin = typeContrat === 'CDI' ? '' : (dateFin || '');
    if (currFin !== origFin) return true;
    if (Number(salaire) !== Number(originalContrat.salaire_mensuel || 0)) return true;
    if (normaliseStatutCode(statut) !== normaliseStatutCode(originalContrat.statut)) return true;

    const origArticles = (originalContrat.articles || []).map(a => a.id).sort();
    const currArticles = [...selectedArticleIds].sort();
    if (origArticles.length !== currArticles.length) return true;
    for (let i = 0; i < origArticles.length; i++) {
      if (origArticles[i] !== currArticles[i]) return true;
    }
    return false;
  };

  // â”€â”€â”€ Soumission â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

 const handleSubmit = async () => {
  setSubmitError('');
  setSubmitting(true);

  try {
    if (isEdit) {
      const isModified = checkIfContratModified();

      if (isModified) {
        try {
          const resWord = await api.post(
            `/contrats/${id}/generer-word`,
            null,
            { responseType: 'blob' }
          );

          const blob = new Blob(
            [resWord.data],
            {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }
          );

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;

          const ref =
            originalContrat?.reference ||
            `CTR-${id}`;

          link.setAttribute(
            'download',
            `Contrat_${ref}_avant_modification.docx`
          );

          document.body.appendChild(link);
          link.click();
          link.remove();

          window.URL.revokeObjectURL(url);

          await new Promise(
            (resolve) => setTimeout(resolve, 600)
          );
        } catch (dlErr) {
          console.warn(
            'Échec du téléchargement automatique de l’ancienne version Word:',
            dlErr
          );
        }
      }

      const payload = {
        type_contrat: typeContrat,
        date_debut: dateDebut || undefined,
        salaire_mensuel: Number(salaire),
        statut,
        article_ids: selectedArticleIds,
      };

      if (typeContrat !== 'CDI') {
        payload.date_fin = dateFin;
      } else {
        payload.date_fin = null;
      }

      await api.put(
        `/contrats/${id}`,
        payload
      );

    } else {
  if (!selectedCandidat?.id) {
    throw new Error("Veuillez sélectionner un candidat accepté.");
  }

  const candidatId = Number(selectedCandidat.id);

  if (!Number.isInteger(candidatId) || candidatId <= 0) {
    throw new Error("L'identifiant du candidat sélectionné est invalide.");
  }

  const payload = {
    candidat_id: candidatId,
    type_contrat: typeContrat,
    date_debut: dateDebut,
    date_fin: typeContrat === "CDI" ? null : dateFin,
    salaire_mensuel: Number(salaire),
    statut: "BROUILLON",
    article_ids: selectedArticleIds.map(Number),
  };

  console.log("[ContratForm] Candidat sélectionné :", {
    id: selectedCandidat.id,
    nom: selectedCandidat.first_name,
    prenom: selectedCandidat.last_name,
    email: selectedCandidat.email,
  });

  console.log("[ContratForm] Payload création :", payload);

  const response = await api.post("/contrats", payload);

  console.log("[ContratForm] Contrat créé :", response.data);

  if (!response.data?.candidate_id && !response.data?.candidat_id) {
    console.warn(
      "[ContratForm] Le backend n'a pas renvoyé l'identifiant du candidat."
    );
  }
}

    navigate('/rh/contracts');

  } catch (err) {
    console.error(
      '[ContratForm] Erreur enregistrement contrat:',
      err?.response?.data || err
    );

    console.error(
      '[ContratForm] Detail API:',
      err?.response?.data?.detail
    );

    setSubmitError(
      err?.response?.data?.detail ||
      err?.message ||
      "Erreur lors de l'enregistrement."
    );

  } finally {
    setSubmitting(false);
  }
};
  // â”€â”€â”€ Filtres locaux â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const filteredCandidats = candidats.filter(candidate => {
    const firstName = candidate?.first_name || candidate?.prenom || '';
    const lastName = candidate?.last_name || candidate?.nom || '';
    const email = candidate?.email || '';
    const phone = candidate?.phone || candidate?.telephone || '';
    const matricule = candidate?.matricule || '';

    const fullText = [
      firstName,
      lastName,
      email,
      phone,
      matricule,
    ]
      .join(' ')
      .toLowerCase()
      .trim();

    return fullText.includes(
      searchCandidat.toLowerCase().trim()
    );
  });

  const compatibleArticles = articles.filter(a => isArticleCompatible(a, typeContrat));
  const filteredArticles = compatibleArticles.filter(a =>
    `${a.code} ${a.titre} ${a.contenu_par_defaut || ''}`
      .toLowerCase().includes(searchArticle.toLowerCase())
  );

  // â”€â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div className="app-container">
    
      <main className="main-content">
      
        <div className="content-padding">

          {/* Stepper */}
          <div className="cf-stepper glass-card">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isDone    = step > s.id;
              const isCurrent = step === s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className={`cf-step ${isCurrent ? 'current' : ''} ${isDone ? 'done' : ''}`}>
                    <div className="cf-step-circle">
                      {isDone ? <Check size={16} /> : <Icon size={16} />}
                    </div>
                    <span className="cf-step-label">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`cf-step-connector ${step > s.id ? 'done' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Contenu de l'étape */}
          <div className="cf-card glass-card">

            {/* ─── ÉTAPE 1 : Candidat accepté ─────────────────────────────── */}
            {step === 1 && (
              <div className="cf-step-content">
                <h3 className="cf-step-title">
                  <User size={20} />
                  Sélectionner un candidat accepté
                </h3>

                {isEdit && selectedCandidat ? (
                  <div className="cf-readonly-employe">
                    <div className="cf-employe-avatar">
                      {(
                        (selectedCandidat.first_name || selectedCandidat.prenom || '')[0] || ''
                      ) + (
                        (selectedCandidat.last_name || selectedCandidat.nom || '')[0] || ''
                      )}
                    </div>

                    <div className="cf-employe-info">
                      <span className="cf-employe-name">
                        {selectedCandidat.first_name || selectedCandidat.prenom || ''}{' '}
                        {selectedCandidat.last_name || selectedCandidat.nom || ''}
                      </span>

                      <span className="cf-employe-meta">
                        {selectedCandidat.email || 'Email non renseigné'}
                        {selectedCandidat.phone || selectedCandidat.telephone
                          ? ` · ${selectedCandidat.phone || selectedCandidat.telephone}`
                          : ''}
                      </span>
                    </div>

                    <span className="cf-readonly-badge">
                      Candidat accepté
                    </span>
                  </div>
                ) : (
                  <>
                    {selectedCandidat && (
                      <div className="cf-selected-employe">
                        <CheckCircle2 size={18} className="cf-selected-icon" />

                        <div className="cf-employe-avatar sm">
                          {(
                            (selectedCandidat.first_name || selectedCandidat.prenom || '')[0] || ''
                          ) + (
                            (selectedCandidat.last_name || selectedCandidat.nom || '')[0] || ''
                          )}
                        </div>

                        <div>
                          <span className="cf-employe-name">
                            {selectedCandidat.first_name || selectedCandidat.prenom || ''}{' '}
                            {selectedCandidat.last_name || selectedCandidat.nom || ''}
                          </span>

                          <span className="cf-employe-meta">
                            {selectedCandidat.email || 'Email non renseigné'}
                          </span>
                        </div>

                        <button
                          type="button"
                          className="cf-clear-btn"
                          onClick={() => setSelectedCandidat(null)}
                        >
                          Changer
                        </button>
                      </div>
                    )}

                    <div className="cf-search-wrapper">
                      <Search size={15} className="cf-search-icon" />

                      <input
                        type="text"
                        placeholder="Rechercher un candidat accepté..."
                        value={searchCandidat}
                        onChange={e => setSearchCandidat(e.target.value)}
                        className="cf-search-input"
                      />
                    </div>

                    {loadingCandidats ? (
                      <div className="cf-loading">
                        <Loader2 size={20} className="cf-spin" />
                        Chargement des candidats acceptés...
                      </div>
                    ) : (
                      <div className="cf-employe-list">
                        {filteredCandidats.length === 0 ? (
                          <p className="cf-empty-msg">
                            Aucun candidat avec le statut ACCEPTED n'est disponible.
                          </p>
                        ) : (
                          filteredCandidats.map(candidate => {
                            const firstName =
                              candidate.first_name || candidate.prenom || '';

                            const lastName =
                              candidate.last_name || candidate.nom || '';

                            const isSelected =
                              selectedCandidat?.id === candidate.id;

                            return (
                              <div
                                key={candidate.id}
                                className={`cf-employe-row ${isSelected ? 'selected' : ''}`}
                                onClick={() => setSelectedCandidat(candidate)}
                              >
                                <div className="cf-employe-avatar sm">
                                  {(firstName[0] || '') + (lastName[0] || '')}
                                </div>

                                <div className="cf-employe-row-info">
                                  <span className="cf-employe-name">
                                    {firstName} {lastName}
                                  </span>

                                  <span className="cf-employe-meta">
                                    {candidate.email || 'Email non renseigné'}
                                    {candidate.phone
                                      ? ` · ${candidate.phone}`
                                      : ''}
                                  </span>

                                  <span
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      color: '#166534',
                                      marginTop: '2px',
                                      display: 'inline-block',
                                    }}
                                  >
                                    Statut : ACCEPTED
                                  </span>
                                </div>

                                {isSelected && (
                                  <CheckCircle2
                                    size={18}
                                    className="cf-selected-icon"
                                  />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {!validateStep(1) && (
                      <p className="cf-hint">
                        <AlertCircle size={14} />
                        Sélectionnez un candidat accepté pour continuer.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* â”€â”€â”€ è‰TAPE 2 : Informations â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {step === 2 && (
              <div className="cf-step-content">
                <h3 className="cf-step-title">
                  <Calendar size={20} />
                  Informations contractuelles
                </h3>

                <div className="cf-form-grid">
                  <div className="cf-form-group">
                    <label className="cf-label">Type de contrat <span className="cf-required">*</span></label>
                    <select
                      value={typeContrat}
                      onChange={e => handleTypeContratChange(e.target.value)}
                      className="cf-input"
                    >
                      <option value="">Sélectionnez un type</option>
                      {TYPES_CONTRAT.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="cf-form-group">
                    <label className="cf-label">Date de début <span className="cf-required">*</span></label>
                    <input
                      type="date"
                      value={dateDebut}
                      onChange={e => setDateDebut(e.target.value)}
                      className="cf-input"
                    />
                  </div>

                  {typeContrat !== 'CDI' && (
                    <div className="cf-form-group">
                      <label className="cf-label">Date de fin <span className="cf-required">*</span></label>
                      <input
                        type="date"
                        value={dateFin}
                        onChange={e => setDateFin(e.target.value)}
                        className="cf-input"
                        min={dateDebut || undefined}
                      />
                    </div>
                  )}

                  {typeContrat === 'CDI' && (
                    <div className="cf-form-group">
                      <label className="cf-label">Date de fin</label>
                      <div className="cf-cdi-indefini">
                        Contrat à durée indéterminée - aucune date de fin
                      </div>
                    </div>
                  )}

                  <div className="cf-form-group">
                    <label className="cf-label">
                      <DollarSign size={14} />
                      Salaire mensuel (DT) <span className="cf-required">*</span>
                    </label>
                    <input
                      type="number"
                      value={salaire}
                      onChange={e => setSalaire(e.target.value)}
                      placeholder="Ex : 1200"
                      min="1"
                      className="cf-input"
                    />
                  </div>

                  {isEdit && (() => {
                    const availableStatuts = getAvailableStatuts(initialStatut, typeContrat);
                    const isFinal = availableStatuts.length <= 1;
                    return (
                      <div className="cf-form-group">
                        <label className="cf-label">Statut du contrat</label>
                        <select
                          value={statut}
                          onChange={e => setStatut(e.target.value)}
                          className="cf-input"
                          disabled={isFinal}
                        >
                          {availableStatuts.map(s => (
                            <option key={s.code} value={s.code}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                        {isFinal && (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                            Statut final - aucune modification ultérieure possible.
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {!validateStep(2) && (
                  <p className="cf-hint">
                    <AlertCircle size={14} />
                    {!typeContrat
                      ? ' Le type de contrat est obligatoire.'
                      : typeContrat !== 'CDI' && (!dateFin || dateFin <= dateDebut)
                        ? ' La date de fin est obligatoire et doit èªtre postérieure à la date de début.'
                        : ' La date de début et le salaire sont obligatoires.'}
                  </p>
                )}
              </div>
            )}

            {/* â”€â”€â”€ è‰TAPE 3 : Articles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {step === 3 && (
              <div className="cf-step-content">
                <h3 className="cf-step-title">
                  <BookOpen size={20} />
                  Articles à inclure
                  <span className="cf-articles-count">
                    {selectedArticleIds.length} sélectionné(s)
                  </span>
                </h3>

                <div className="cf-search-wrapper">
                  <Search size={15} className="cf-search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher un article..."
                    value={searchArticle}
                    onChange={e => setSearchArticle(e.target.value)}
                    className="cf-search-input"
                  />
                </div>

                {loadingArticles ? (
                  <div className="cf-loading"><Loader2 size={20} className="cf-spin" /> Chargement...</div>
                ) : compatibleArticles.length === 0 ? (
                  <p className="cf-empty-msg">
                    Aucun article spécifique n'est actuellement disponible pour ce type de contrat ({typeContrat || 'sélectionné'}).
                  </p>
                ) : filteredArticles.length === 0 ? (
                  <p className="cf-empty-msg">Aucun article ne correspond à votre recherche.</p>
                ) : (
                  <div className="cf-articles-list">
                    {filteredArticles.map(a => {
                      const isSelected = selectedArticleIds.includes(a.id);
                      return (
                        <div
                          key={a.id}
                          className={`cf-article-row ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleArticle(a.id)}
                        >
                          <div className={`cf-article-checkbox ${isSelected ? 'checked' : ''}`}>
                            {isSelected && <Check size={12} />}
                          </div>
                          <div className="cf-article-info">
                            <div className="cf-article-header-row">
                              <span className="cf-article-code">{a.code}</span>
                              <span className="cf-article-titre">{a.titre}</span>
                              {a.types_contrat && Array.isArray(a.types_contrat) && a.types_contrat.length > 0 ? (
                                <span className="cf-article-types-badge">
                                  {a.types_contrat.map(t => (
                                    <span key={t} className="cf-article-badge-item">{t}</span>
                                  ))}
                                </span>
                              ) : (
                                <span className="cf-article-types-badge generic">Tous types</span>
                              )}
                            </div>
                            {a.contenu_par_defaut && (
                              <p className="cf-article-preview">
                                {a.contenu_par_defaut.substring(0, 100)}
                                {a.contenu_par_defaut.length > 100 ? '...' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="cf-articles-hint">
                  Les articles sélectionnés seront inclus dans le document Word du contrat.
                  La sélection est optionnelle.
                </p>
              </div>
            )}

            {/* â”€â”€â”€ Erreur de soumission â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {submitError && (
              <div className="cf-submit-error">
                <AlertCircle size={16} />
                {submitError}
              </div>
            )}

            {/* â”€â”€â”€ Navigation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="cf-nav-bar">
              <button
                className="cf-nav-btn secondary"
                onClick={() => step === 1 ? navigate('/contrats') : goPrev()}
              >
                <ChevronLeft size={16} />
                {step === 1 ? 'Annuler' : 'Précédent'}
              </button>

              <div className="cf-step-indicator">
                è‰tape {step} / {STEPS.length}
              </div>

              {step < STEPS.length ? (
                <button
                  className="cf-nav-btn primary"
                  onClick={goNext}
                  disabled={!validateStep(step)}
                >
                  Suivant
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  className="cf-nav-btn primary"
                  onClick={handleSubmit}
                  disabled={submitting || !validateStep(1) || !validateStep(2)}
                >
                  {submitting ? (
                    <><Loader2 size={15} className="cf-spin" /> Enregistrement...</>
                  ) : (
                    <><Check size={15} /> {isEdit ? 'Enregistrer' : 'Créer le contrat'}</>
                  )}
                </button>
              )}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}



